import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

/**
 * BookingForm — sticky sidebar booking form used on the Tour Detail page.
 * Posts to /api/bookings.
 */
export default function BookingForm({ tour, buttonLabel = "Book This Adventure" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    travellers: 2,
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/bookings`, {
        tour_id: tour.id,
        tour_title: tour.title,
        ...form,
        travellers: parseInt(form.travellers, 10)
      });
      toast({
        title: "Enquiry received",
        description: "Our trip captain will call you within 24 hours."
      });
      setForm({ name: "", email: "", phone: "", date: "", travellers: 2, message: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again in a moment.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="sticky top-24 bg-white rounded-2xl border border-[#E5DDCB] p-5 sm:p-6 shadow-sm">
      <div className="pb-4 mb-5 border-b border-[#E5DDCB]">
        <span className="text-xs text-[#6B8E5A] uppercase tracking-wider">Starting</span>
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-3xl sm:text-4xl font-bold text-[#1F4D3A]">
            ₹{tour.price.toLocaleString("en-IN")}
          </span>
          <span className="text-sm text-[#5A4A38]">per person</span>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input required value={form.name} onChange={update("name")} placeholder="Full name" className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
        <input required type="email" value={form.email} onChange={update("email")} placeholder="Email" className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
        <input required value={form.phone} onChange={update("phone")} placeholder="Phone" className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
        <div className="grid grid-cols-2 gap-3">
          <input required type="date" value={form.date} onChange={update("date")} className="px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
          <input required type="number" min="1" max="20" value={form.travellers} onChange={update("travellers")} placeholder="Travellers" className="px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
        </div>
        <textarea value={form.message} onChange={update("message")} placeholder="Anything else we should know?" rows={3} className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A] resize-none" />
        <Button type="submit" disabled={loading} className="btn-primary w-full bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full h-12">
          {loading ? "Sending…" : buttonLabel}
        </Button>
        <p className="text-[11px] text-center text-[#6B8E5A] pt-1">No payment now · We call within 24 hrs</p>
      </form>
    </div>
  );
}
